/**
 * Freshness Prediction Agent
 * Predicts produce freshness and optimal consumption windows
 */

interface ProduceItem {
  name: string;
  category: 'fruit' | 'vegetable' | 'herb' | 'dairy' | 'other';
  harvestDate?: Date;
  purchaseDate: Date;
  storageMethod: 'counter' | 'refrigerator' | 'freezer' | 'pantry';
}

interface FreshnessPrediction {
  item: string;
  freshnessScore: number; // 0-100
  daysRemaining: number;
  optimalUseBy: Date;
  status: 'peak' | 'good' | 'use-soon' | 'past-prime';
  storageTips: string[];
  usageSuggestions: string[];
}

interface StorageGuide {
  item: string;
  optimalStorage: string;
  shelfLife: {
    counter?: number;
    refrigerator?: number;
    freezer?: number;
    pantry?: number;
  };
  signs: {
    fresh: string[];
    spoiled: string[];
  };
  tips: string[];
}

class FreshnessPredictionAgent {
  private produceDatabase: { [key: string]: StorageGuide } = {
    tomatoes: {
      item: 'Tomatoes',
      optimalStorage: 'Store at room temperature until ripe, then refrigerate',
      shelfLife: { counter: 7, refrigerator: 14 },
      signs: {
        fresh: ['Firm but slightly soft', 'Vibrant color', 'Fresh stem smell'],
        spoiled: ['Soft spots', 'Mold', 'Wrinkled skin', 'Sour smell'],
      },
      tips: [
        'Store stem-side down to prevent moisture loss',
        'Keep away from direct sunlight',
        'Bring to room temperature before eating for best flavor',
      ],
    },
    lettuce: {
      item: 'Lettuce',
      optimalStorage: 'Refrigerator in crisper drawer',
      shelfLife: { refrigerator: 7 },
      signs: {
        fresh: ['Crisp leaves', 'Bright green color', 'No wilting'],
        spoiled: ['Slimy leaves', 'Brown edges', 'Foul smell'],
      },
      tips: [
        'Wrap in paper towels to absorb excess moisture',
        'Store in sealed container or bag',
        'Wash only before using',
      ],
    },
    strawberries: {
      item: 'Strawberries',
      optimalStorage: 'Refrigerator unwashed',
      shelfLife: { counter: 2, refrigerator: 5, freezer: 180 },
      signs: {
        fresh: ['Bright red color', 'Firm texture', 'Fresh green caps'],
        spoiled: ['Mold spots', 'Mushy texture', 'Dull color'],
      },
      tips: [
        'Do not wash until ready to eat',
        'Remove any moldy berries immediately',
        'Store in single layer for best results',
      ],
    },
    basil: {
      item: 'Basil',
      optimalStorage: 'Room temperature in water like flowers',
      shelfLife: { counter: 7, refrigerator: 3 },
      signs: {
        fresh: ['Vibrant green', 'Strong aroma', 'No wilting'],
        spoiled: ['Black spots', 'Wilted leaves', 'Slimy stems'],
      },
      tips: [
        'Trim stems and place in water at room temperature',
        'Cover loosely with plastic bag',
        'Change water every few days',
      ],
    },
    eggs: {
      item: 'Eggs',
      optimalStorage: 'Refrigerator in original carton',
      shelfLife: { refrigerator: 35 },
      signs: {
        fresh: ['Sinks in water', 'Thick egg white when cracked', 'No smell'],
        spoiled: ['Floats in water', 'Watery whites', 'Sulfur smell'],
      },
      tips: [
        'Keep in original carton to prevent odor absorption',
        'Store on shelf, not in door',
        'Use water test to check freshness',
      ],
    },
    peppers: {
      item: 'Peppers',
      optimalStorage: 'Refrigerator crisper drawer',
      shelfLife: { counter: 5, refrigerator: 14 },
      signs: {
        fresh: ['Firm skin', 'Bright color', 'Fresh green stem'],
        spoiled: ['Wrinkled skin', 'Soft spots', 'Mold'],
      },
      tips: [
        'Store unwashed in crisper drawer',
        'Cut peppers should be stored in airtight container',
        'Green peppers last longer than colored varieties',
      ],
    },
    cucumbers: {
      item: 'Cucumbers',
      optimalStorage: 'Refrigerator wrapped or in container',
      shelfLife: { counter: 3, refrigerator: 10 },
      signs: {
        fresh: ['Firm texture', 'Bright green', 'No soft spots'],
        spoiled: ['Soft or mushy', 'Yellowing', 'Slimy skin'],
      },
      tips: [
        'Wrap in plastic or store in sealed container',
        'Keep away from ethylene-producing fruits',
        'Store cut cucumbers in water',
      ],
    },
    carrots: {
      item: 'Carrots',
      optimalStorage: 'Refrigerator in water or sealed bag',
      shelfLife: { refrigerator: 21, freezer: 365 },
      signs: {
        fresh: ['Firm and crisp', 'Bright orange', 'Fresh green tops'],
        spoiled: ['Soft or rubbery', 'Black spots', 'Slimy texture'],
      },
      tips: [
        'Remove green tops before storing',
        'Store in water for maximum crispness',
        'Blanch before freezing',
      ],
    },
  };

  /**
   * Predict freshness for a produce item
   */
  predictFreshness(item: ProduceItem): FreshnessPrediction {
    const guide = this.getStorageGuide(item.name);
    const daysSincePurchase = this.getDaysSince(item.purchaseDate);
    const expectedShelfLife = guide.shelfLife[item.storageMethod] || 7;
    const daysRemaining = Math.max(0, expectedShelfLife - daysSincePurchase);

    const freshnessScore = Math.max(0, Math.min(100, (daysRemaining / expectedShelfLife) * 100));

    let status: FreshnessPrediction['status'];
    if (freshnessScore >= 80) status = 'peak';
    else if (freshnessScore >= 50) status = 'good';
    else if (freshnessScore >= 20) status = 'use-soon';
    else status = 'past-prime';

    const optimalUseBy = new Date(item.purchaseDate);
    optimalUseBy.setDate(optimalUseBy.getDate() + expectedShelfLife);

    return {
      item: item.name,
      freshnessScore: Math.round(freshnessScore),
      daysRemaining,
      optimalUseBy,
      status,
      storageTips: guide.tips,
      usageSuggestions: this.getUsageSuggestions(item.name, status),
    };
  }

  /**
   * Get storage guide for item
   */
  getStorageGuide(itemName: string): StorageGuide {
    const normalized = itemName.toLowerCase();
    return this.produceDatabase[normalized] || this.getDefaultGuide(itemName);
  }

  /**
   * Get default guide for unknown items
   */
  private getDefaultGuide(itemName: string): StorageGuide {
    return {
      item: itemName,
      optimalStorage: 'Refrigerator',
      shelfLife: { refrigerator: 7 },
      signs: {
        fresh: ['Firm texture', 'Good color', 'No off odors'],
        spoiled: ['Soft spots', 'Discoloration', 'Bad smell'],
      },
      tips: [
        'Store in refrigerator crisper drawer',
        'Use within a week for best quality',
        'Check regularly for signs of spoilage',
      ],
    };
  }

  /**
   * Calculate days since date
   */
  private getDaysSince(date: Date): number {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  /**
   * Get usage suggestions based on freshness status
   */
  private getUsageSuggestions(itemName: string, status: FreshnessPrediction['status']): string[] {
    const normalized = itemName.toLowerCase();

    const suggestions: { [key: string]: { [key: string]: string[] } } = {
      tomatoes: {
        peak: ['Fresh salads', 'Caprese', 'Sandwiches', 'Bruschetta'],
        good: ['Pasta sauce', 'Salsa', 'Grilled', 'Soup'],
        'use-soon': ['Tomato sauce', 'Stewed tomatoes', 'Soup base'],
        'past-prime': ['Compost', 'Freeze for future sauces'],
      },
      lettuce: {
        peak: ['Fresh salads', 'Wraps', 'Sandwiches'],
        good: ['Salads', 'Garnish'],
        'use-soon': ['Smoothies', 'Sauteed'],
        'past-prime': ['Compost'],
      },
      strawberries: {
        peak: ['Fresh eating', 'Fruit salad', 'With cream'],
        good: ['Smoothies', 'Yogurt topping', 'Pancakes'],
        'use-soon': ['Jam', 'Baking', 'Freeze'],
        'past-prime': ['Compost', 'Smoothies'],
      },
    };

    return suggestions[normalized]?.[status] || [
      'Use as planned',
      'Consider freezing if not using soon',
      'Check for freshness before using',
    ];
  }

  /**
   * Get batch freshness predictions
   */
  predictBatchFreshness(items: ProduceItem[]): FreshnessPrediction[] {
    return items.map(item => this.predictFreshness(item));
  }

  /**
   * Get items that need attention
   */
  getItemsNeedingAttention(items: ProduceItem[]): FreshnessPrediction[] {
    const predictions = this.predictBatchFreshness(items);
    return predictions.filter(p => p.status === 'use-soon' || p.status === 'past-prime');
  }

  /**
   * Calculate optimal shopping schedule
   */
  calculateShoppingSchedule(
    items: string[],
    storageMethod: 'counter' | 'refrigerator' = 'refrigerator'
  ): { item: string; buyEvery: number; optimalDay: string }[] {
    return items.map(item => {
      const guide = this.getStorageGuide(item);
      const shelfLife = guide.shelfLife[storageMethod] || 7;

      return {
        item,
        buyEvery: Math.max(3, shelfLife - 2), // Buy before it goes bad
        optimalDay: this.getOptimalShoppingDay(shelfLife),
      };
    });
  }

  /**
   * Get optimal shopping day based on shelf life
   */
  private getOptimalShoppingDay(shelfLife: number): string {
    if (shelfLife <= 5) return 'Every 3-4 days';
    if (shelfLife <= 10) return 'Weekly';
    return 'Bi-weekly';
  }

  /**
   * Get ethylene sensitivity info
   */
  getEthyleneSensitivity(itemName: string): {
    produces: boolean;
    sensitive: boolean;
    storageNote: string;
  } {
    const ethyleneProducers = ['apples', 'bananas', 'tomatoes', 'avocados', 'melons'];
    const ethyleneSensitive = ['lettuce', 'broccoli', 'carrots', 'cucumbers', 'herbs'];

    const normalized = itemName.toLowerCase();
    const produces = ethyleneProducers.includes(normalized);
    const sensitive = ethyleneSensitive.includes(normalized);

    let storageNote = 'Can be stored with most produce';
    if (produces) {
      storageNote = 'Produces ethylene - store away from sensitive items';
    } else if (sensitive) {
      storageNote = 'Ethylene sensitive - store away from ripening fruits';
    }

    return { produces, sensitive, storageNote };
  }
}

const freshnessPredictionAgent = new FreshnessPredictionAgent();
export default freshnessPredictionAgent;
